import {
  onceSafeUnlisten,
  pluginCoreIsTauri,
  pluginCoreLogger,
  type UnlistenFn,
} from '@heidi/plugin-core';

export const PROJECT_AGENT_REGISTRY_POKE_EVENT = 'project-agent:registry-poke';

let projectAgentIpcSessionId: string | null = null;

const getProjectAgentIpcSessionId = (): string => {
  projectAgentIpcSessionId ??=
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  return projectAgentIpcSessionId;
};

const canUseTauriEvents = (): boolean => {
  try {
    return pluginCoreIsTauri();
  } catch (error) {
    pluginCoreLogger().debug('[project-agent-ipc] Tauri event bridge unavailable', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

export interface ProjectAgentRegistryPokePayload {
  sourceSessionId: string;
}

const noopUnlisten: UnlistenFn = () => undefined;

const logAlreadyRemovedUnlisten = (error: unknown): void => {
  pluginCoreLogger().debug('[project-agent-ipc] Tauri event listener already removed', {
    error: error instanceof Error ? error.message : String(error),
  });
};

const readString = (obj: Record<string, unknown>, key: string): string | null => {
  const value = obj[key];
  return typeof value === 'string' && value ? value : null;
};

const parseRegistryPokePayload = (
  payload: unknown
): ProjectAgentRegistryPokePayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const obj = payload as Record<string, unknown>;
  const sourceSessionId = readString(obj, 'sourceSessionId');
  if (!sourceSessionId) return null;
  return { sourceSessionId };
};

/**
 * Wake other windows to re-read persisted background registry. Best-effort —
 * lost poke heals on next read. Exists because `storage` events don't fire
 * reliably across Tauri webviews (the overlay→main project handoff rides it).
 */
export const emitProjectAgentRegistryPoke = async (): Promise<boolean> => {
  if (!canUseTauriEvents()) return false;
  try {
    const { emit } = await import('@tauri-apps/api/event');
    await emit<ProjectAgentRegistryPokePayload>(PROJECT_AGENT_REGISTRY_POKE_EVENT, {
      sourceSessionId: getProjectAgentIpcSessionId(),
    });
    return true;
  } catch (error) {
    pluginCoreLogger().warn('[project-agent-ipc] Failed to emit registry poke', {
      error: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
};

export const listenProjectAgentRegistryPoke = async (
  handler: () => void
): Promise<UnlistenFn> => {
  if (!canUseTauriEvents()) return noopUnlisten;
  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<ProjectAgentRegistryPokePayload>(
      PROJECT_AGENT_REGISTRY_POKE_EVENT,
      (event) => {
        const payload = parseRegistryPokePayload(event.payload);
        if (!payload) return;
        if (payload.sourceSessionId === getProjectAgentIpcSessionId()) return;
        handler();
      }
    );
    return onceSafeUnlisten(unlisten, logAlreadyRemovedUnlisten);
  } catch (error) {
    pluginCoreLogger().warn('[project-agent-ipc] Failed to listen for registry pokes', {
      error: error instanceof Error ? error.message : String(error),
    });
    return noopUnlisten;
  }
};
